/**
 * RetailGL — the room and the warehouse rendered by the GPU.
 *
 * The hand-written SVG renderer stays : it is what the quote PDF is drawn with,
 * server-side, and a vector isometric is what a customer signs. But an SVG
 * pattern is an affine map, and a perspective view is not affine — that is why
 * the grain slid across the furniture, why the floor had to be cut into tiles,
 * and why the lighting could never be more than three fixed shades per box.
 *
 * WebGL removes the whole class of problem at once :
 *
 *   * texture coordinates are interpolated with perspective correction, so the
 *     grain is welded to the surface and cannot drift ;
 *   * a depth buffer replaces the painter's algorithm, so nothing can be
 *     painted over anything ;
 *   * normal and roughness maps let a light actually catch the material ;
 *   * shadows are cast rather than faked with a flat quad.
 *
 * three.js is loaded on demand, from the app's own assets — never a CDN, so it
 * works on a closed network — and only when a 3D view is opened. Desk pages
 * that never show one pay nothing.
 */

/* global frappe */

const THREE_URL = "js/vendor/three.module.min.js";
const ASSET_ROOT = "/assets/ioi_retail_suite/";

/**
 * How much real surface one texture tile covers, per material.
 *
 * Mirrors `engine/materials.TILE_CM`, which is the authority — the server sends
 * the table with the material catalogue and it overrides this. The copy is here
 * so a viewer mounted without that call still looks right rather than wrapping
 * a 14 cm fabric weave over half a metre.
 */
const TILE_FALLBACK = {
	"wood-light": 65, "wood-mid": 65, "wood-dark": 65,
	laminate: 90, paint: 45,
	fabric: 14, felt: 12, carpet: 32,
	metal: 26, carton: 42, concrete: 150,
};

let threePromise = null;

/**
 * Load three.js once, lazily, and share the promise.
 *
 * The specifier is built at runtime so the bundler leaves it alone : three
 * stays a separate asset instead of being inlined into every desk page. It has
 * to be resolved against the document, because a dynamic import of a bare
 * `js/vendor/...` is a module specifier, not a path, and browsers refuse it.
 */
function loadThree(root) {
	if (threePromise) return threePromise;
	// an empty root is a legitimate value — a relative mount — so it must not
	// fall through to the default the way a falsy test would
	const base = root === undefined || root === null ? ASSET_ROOT : root;
	const raw = base + THREE_URL;
	const url = typeof document !== "undefined" && document.baseURI
		? new URL(raw, document.baseURI).href
		: raw;
	threePromise = import(/* webpackIgnore: true */ url).catch((error) => {
		// never cache a failure : a reload, or a second view, must be able to
		// try again instead of inheriting the first miss for ever
		threePromise = null;
		throw error;
	});
	return threePromise;
}

class RetailGL {
	constructor(container, options = {}) {
		this.container = container;
		this.options = Object.assign(
			{
				assetRoot: ASSET_ROOT,
				textureTileCm: 50,    // fallback, only for a material with no entry
				materialTiles: null,  // {material: cm}, from api.material_catalog
				// In a walkthrough the material is what sells the room ; the
				// family colour is only there to hint. Above ~8 % a white
				// laminate bookcase comes out blue.
				tintStrength: 0.06,
				shadows: true,
				maxPixelRatio: 2,
				onSelect: null,
				onMove: null,
				onMoveEnd: null,
				onContext: null,
			},
			options
		);
		this.scene = null;
		this.ready = false;
		this.selected = null;
		this.mode = "orbit";
		this.eye = { x: 0, y: 0, height: 175, yaw: 90, pitch: 0, fov: 72 };
		this.orbit = { yaw: -35, pitch: 34, distance: 900 };
		this._materials = new Map();
		this._objects = [];
		this._keys = new Set();
		this._needsFrame = false;
	}

	// ------------------------------------------------------------------ setup
	async mount() {
		const THREE = await loadThree(this.options.assetRoot);
		this.THREE = THREE;

		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.options.maxPixelRatio));
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 0.92;
		if (this.options.shadows) {
			this.renderer.shadowMap.enabled = true;
			this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		}
		this.canvas = this.renderer.domElement;
		this.canvas.className = "r3d-canvas";
		this.canvas.setAttribute("tabindex", "0");
		this.container.appendChild(this.canvas);

		this.world = new THREE.Scene();
		this.world.background = new THREE.Color(0xeef1f5);

		// z is up in every other part of this app, so the whole scene is tipped
		// once here rather than swapping axes at a hundred call sites
		this.root = new THREE.Group();
		this.root.rotation.x = -Math.PI / 2;
		this.world.add(this.root);

		this.camera = new THREE.PerspectiveCamera(this.eye.fov, 1, 5, 20000);
		this.world.add(this.camera);

		this._buildLights();
		this._buildEnvironment();
		this._bind();
		this._observeSize();
		this.ready = true;
		return this;
	}

	_buildLights() {
		const THREE = this.THREE;
		this.hemi = new THREE.HemisphereLight(0xf4f7fb, 0xa9b0b8, 1.05);
		this.world.add(this.hemi);

		this.sun = new THREE.DirectionalLight(0xfff1dc, 2.45);
		this.sun.castShadow = this.options.shadows;
		this.sun.shadow.mapSize.set(2048, 2048);
		// tight values : a large normal bias pushes the sample off the surface
		// and paints light where a shadow belongs
		this.sun.shadow.bias = -0.0004;
		this.sun.shadow.normalBias = 0.6;
		this.world.add(this.sun);
		this.world.add(this.sun.target);

		// a second, dimmer light from the opposite side stops the shadow side
		// of every object from going to a flat dead grey
		this.fill = new THREE.DirectionalLight(0xd7e3ff, 0.42);
		this.world.add(this.fill);
	}

	/**
	 * A tiny gradient sky, turned into an environment map.
	 *
	 * Standard materials need something to reflect or metal looks like paint
	 * and everything else looks lifeless. Generating it beats shipping an HDR.
	 */
	_buildEnvironment() {
		const THREE = this.THREE;
		const size = 256;
		const canvas = document.createElement("canvas");
		canvas.width = size;
		canvas.height = size;
		const context = canvas.getContext("2d");
		const gradient = context.createLinearGradient(0, 0, 0, size);
		gradient.addColorStop(0, "#ffffff");
		gradient.addColorStop(0.45, "#e8eef6");
		gradient.addColorStop(0.55, "#c9d2dc");
		gradient.addColorStop(1, "#8f979f");
		context.fillStyle = gradient;
		context.fillRect(0, 0, size, size);

		const texture = new THREE.CanvasTexture(canvas);
		texture.mapping = THREE.EquirectangularReflectionMapping;
		texture.colorSpace = THREE.SRGBColorSpace;
		const pmrem = new THREE.PMREMGenerator(this.renderer);
		this.world.environment = pmrem.fromEquirectangular(texture).texture;
		pmrem.dispose();
		texture.dispose();
	}

	// -------------------------------------------------------------- materials
	_material(key, side) {
		const cacheKey = side ? key + "|" + side : key;
		if (this._materials.has(cacheKey)) return this._materials.get(cacheKey);
		const THREE = this.THREE;
		const route = this.options.assetRoot + "textures/";
		const loader = this._loader || (this._loader = new THREE.TextureLoader());
		const repeat = (texture, srgb) => {
			texture.wrapS = THREE.RepeatWrapping;
			texture.wrapT = THREE.RepeatWrapping;
			texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
			if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
			texture.needsUpdate = true;
			this._needsFrame = true;
			this.request();
			return texture;
		};

		const material = new THREE.MeshStandardMaterial({
			vertexColors: true,
			roughness: 0.85,
			metalness: key === "metal" ? 0.8 : 0.02,
		});
		if (key) {
			material.map = repeat(loader.load(route + key + ".jpg"), true);
			material.normalMap = repeat(loader.load(route + key + "_n.jpg"), false);
			// strong enough to catch the light, not so strong the wall looks quilted
			material.normalScale = new THREE.Vector2(0.55, 0.55);
			material.roughnessMap = repeat(loader.load(route + key + "_r.jpg"), false);
		}
		if (side) material.side = side;
		this._materials.set(cacheKey, material);
		return material;
	}

	/**
	 * One flat quad from four corners, wound so the normal points where asked.
	 *
	 * The room shell is built from these rather than from boxes : a plane is
	 * one-sided, so a wall is solid from inside the room and simply absent from
	 * outside it. That is what makes the orbit view a cutaway instead of a
	 * closed crate seen from the wrong side.
	 */
	_quad(corners, key, uSpan, vSpan) {
		const THREE = this.THREE;
		const [p0, p1, p2, p3] = corners;
		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array([
			...p0, ...p1, ...p2,
			...p0, ...p2, ...p3,
		]), 3));
		const T = this._tileFor(key);
		const u = uSpan / T;
		const v = vSpan / T;
		geometry.setAttribute("uv", new THREE.BufferAttribute(new Float32Array([
			0, 0, u, 0, u, v,
			0, 0, u, v, 0, v,
		]), 2));
		geometry.computeVertexNormals();
		return geometry;
	}

	/**
	 * Rewrite the UVs so one texture tile always covers the same real surface.
	 *
	 * Without this a 40 cm bedside table and a 240 cm wardrobe would each get
	 * exactly one tile stretched over them, and neither would look like wood.
	 */
	_tileFor(key) {
		const table = this.options.materialTiles || TILE_FALLBACK;
		return table[key] || TILE_FALLBACK[key] || this.options.textureTileCm;
	}

	_worldScaleUV(geometry, w, d, h, key) {
		const T = this._tileFor(key);
		const uv = geometry.attributes.uv;
		// BoxGeometry(W, H, D) lays its faces out ±X, ±X, ±Y, ±Y, ±Z, ±Z with
		// four vertices each ; here W=w, H=d, D=h
		const spans = [
			[h, d], [h, d],
			[w, h], [w, h],
			[w, d], [w, d],
		];
		for (let face = 0; face < 6; face++) {
			const [su, sv] = spans[face];
			for (let corner = 0; corner < 4; corner++) {
				const index = face * 4 + corner;
				uv.setXY(index, uv.getX(index) * (su / T), uv.getY(index) * (sv / T));
			}
		}
		uv.needsUpdate = true;
		return geometry;
	}

	_paint(geometry, colour, strength) {
		const THREE = this.THREE;
		const base = new THREE.Color(colour || "#b9bfc7");
		// the texture carries the material, the family colour only tints it
		const tint = new THREE.Color(1, 1, 1).lerp(base, strength);
		const count = geometry.attributes.position.count;
		const colours = new Float32Array(count * 3);
		for (let i = 0; i < count; i++) {
			colours[i * 3] = tint.r;
			colours[i * 3 + 1] = tint.g;
			colours[i * 3 + 2] = tint.b;
		}
		geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3));
		return geometry;
	}

	// ------------------------------------------------------------ scene build
	setScene(scene) {
		this.scene = scene;
		if (!this.ready) return this;
		const THREE = this.THREE;

		if (this.content) {
			this.root.remove(this.content);
			this.content.traverse((node) => {
				if (node.geometry) node.geometry.dispose();
			});
		}
		this.content = new THREE.Group();
		this.root.add(this.content);
		this._objects = [];

		const room = scene.room || { w: 400, d: 300, h: 250 };
		this._buildShell(room, !!scene.warehouse);

		for (const box of scene.boxes || []) {
			const key = box.material || "laminate";
			const geometry = new THREE.BoxGeometry(box.w, box.d, box.h);
			this._worldScaleUV(geometry, box.w, box.d, box.h, key);
			// tone is the per-part shade the flat renderer used ; here it only
			// nudges the tint, the light does the rest
			const shade = Math.max(0, Math.min(1, this.options.tintStrength + (box.tone || 0) * 0.15));
			this._paint(geometry, box.color, shade);

			const mesh = new THREE.Mesh(geometry, this._material(key));
			mesh.castShadow = this.options.shadows;
			mesh.receiveShadow = this.options.shadows;
			mesh.position.set(box.x + box.w / 2, box.y + box.d / 2, box.z + box.h / 2);

			const rot = box.rot || [0, 0, 0];
			if (rot[0] % 360 || rot[1] % 360 || rot[2] % 360) {
				const pivot = box.pivot || [box.x + box.w / 2, box.y + box.d / 2, box.z];
				const matrix = new THREE.Matrix4()
					.makeRotationZ((rot[2] * Math.PI) / 180)
					.multiply(new THREE.Matrix4().makeRotationY((rot[1] * Math.PI) / 180))
					.multiply(new THREE.Matrix4().makeRotationX((rot[0] * Math.PI) / 180));
				const offset = new THREE.Vector3(
					mesh.position.x - pivot[0],
					mesh.position.y - pivot[1],
					mesh.position.z - pivot[2]
				).applyMatrix4(matrix);
				mesh.position.set(pivot[0] + offset.x, pivot[1] + offset.y, pivot[2] + offset.z);
				mesh.quaternion.setFromRotationMatrix(matrix);
			}

			mesh.userData.box = box;
			mesh.userData.id = box.parent || box.id;
			this.content.add(mesh);
			this._objects.push(mesh);
		}

		this._frameLights(room);
		this._applySelection();
		this.request();
		return this;
	}

	_buildShell(room, warehouse) {
		const THREE = this.THREE;
		const skin = warehouse
			? { floor: "concrete", wall: "paint" }
			: { floor: "wood-light", wall: "paint" };
		const w = room.w;
		const d = room.d;
		const h = room.h;

		// corners wound so every normal points into the room
		const faces = [
			{ key: skin.floor, side: THREE.DoubleSide, u: w, v: d, shade: 1,
			  corners: [[0, 0, 0], [w, 0, 0], [w, d, 0], [0, d, 0]] },
			{ key: skin.wall, u: h, v: w, shade: 0.99,
			  corners: [[0, 0, 0], [0, 0, h], [w, 0, h], [w, 0, 0]] },
			{ key: skin.wall, u: w, v: h, shade: 0.94,
			  corners: [[0, d, 0], [w, d, 0], [w, d, h], [0, d, h]] },
			{ key: skin.wall, u: d, v: h, shade: 0.96,
			  corners: [[0, 0, 0], [0, d, 0], [0, d, h], [0, 0, h]] },
			{ key: skin.wall, u: h, v: d, shade: 0.92,
			  corners: [[w, 0, 0], [w, 0, h], [w, d, h], [w, d, 0]] },
			{ key: skin.wall, u: d, v: w, shade: 1.02, ceiling: true,
			  corners: [[0, 0, h], [0, d, h], [w, d, h], [w, 0, h]] },
		];

		for (const face of faces) {
			const geometry = this._quad(face.corners, face.key, face.u, face.v);
			// a flat white tint, nudged per wall so the corners of the room read
			const level = Math.min(1, face.shade);
			const count = geometry.attributes.position.count;
			const colours = new Float32Array(count * 3).fill(level);
			geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3));
			const mesh = new THREE.Mesh(geometry, this._material(face.key, face.side));
			// the ceiling faces away from the light : asking it to receive a
			// shadow only buys self-shadowing noise
			mesh.receiveShadow = this.options.shadows && face.key !== "__ceiling";
			if (face.ceiling) mesh.receiveShadow = false;
			this.content.add(mesh);
		}
	}

	_frameLights(room) {
		const span = Math.max(room.w, room.d);
		this.sun.position.set(room.w * 0.25, -room.d * 0.35, room.h * 2.2 + span * 0.4);
		this.sun.target.position.set(room.w * 0.55, room.d * 0.55, 0);
		this.fill.position.set(room.w * 1.4, room.d * 1.4, room.h * 1.2);
		// the shadow frustum has to swallow the whole room seen from the light,
		// not just its longest side — anything outside it comes back lit, which
		// is what put those hard bright patches across the far wall
		const camera = this.sun.shadow.camera;
		const reach = Math.hypot(room.w, room.d) * 0.62 + room.h;
		camera.left = -reach;
		camera.right = reach;
		camera.top = reach;
		camera.bottom = -reach;
		camera.near = 1;
		camera.far = span * 5 + room.h * 5;
		camera.updateProjectionMatrix();
		this.sun.shadow.needsUpdate = true;
	}

	// ------------------------------------------------------------------ camera
	setMode(mode) {
		this.mode = mode === "eye" ? "eye" : "orbit";
		this.request();
		return this;
	}

	setEyeHeight(cm) {
		this.eye.height = Math.max(30, Math.min(280, Number(cm) || 175));
		this.request();
		return this;
	}

	setEye(position) {
		for (const key of ["x", "y", "yaw", "pitch"]) {
			if (position[key] !== undefined) this.eye[key] = Number(position[key]);
		}
		this.request();
		return this;
	}

	zoom(factor) {
		if (this.mode === "eye") this.eye.fov = Math.min(110, Math.max(35, this.eye.fov / factor));
		else this.orbit.distance = Math.max(80, Math.min(9000, this.orbit.distance / factor));
		this.request();
		return this;
	}

	rotate(step = 1) {
		if (this.mode === "eye") this.eye.yaw = (this.eye.yaw + step * 45) % 360;
		else this.orbit.yaw = (this.orbit.yaw + step * 45) % 360;
		this.request();
		return this;
	}

	walk(forward = 0, strafe = 0) {
		const yaw = (this.eye.yaw * Math.PI) / 180;
		const dx = Math.cos(yaw) * forward + Math.sin(yaw) * strafe;
		const dy = Math.sin(yaw) * forward - Math.cos(yaw) * strafe;
		const at = (x, y) => this._standable(x, y);
		if (at(this.eye.x + dx, this.eye.y + dy)) {
			this.eye.x += dx;
			this.eye.y += dy;
		} else if (at(this.eye.x + dx, this.eye.y)) {
			this.eye.x += dx;
		} else if (at(this.eye.x, this.eye.y + dy)) {
			this.eye.y += dy;
		}
		this.request();
		return this;
	}

	_standable(x, y) {
		const room = (this.scene && this.scene.room) || { w: 400, d: 300 };
		if (x < 5 || y < 5 || x > room.w - 5 || y > room.d - 5) return false;
		for (const box of (this.scene && this.scene.boxes) || []) {
			if (box.z > this.eye.height || box.z + box.h < 20) continue;
			const dx = Math.max(box.x - x, 0, x - (box.x + box.w));
			const dy = Math.max(box.y - y, 0, y - (box.y + box.d));
			if (Math.sqrt(dx * dx + dy * dy) < 18) return false;
		}
		return true;
	}

	lookAt(point) {
		const dx = point[0] - this.eye.x;
		const dy = point[1] - this.eye.y;
		const flat = Math.sqrt(dx * dx + dy * dy);
		if (flat < 1) return this;
		this.eye.yaw = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
		this.eye.pitch = Math.max(-80, Math.min(80,
			(Math.atan2(point[2] - this.eye.height, flat) * 180) / Math.PI));
		if (this.mode === "orbit") {
			this.orbit.target = point.slice();
		}
		this.request();
		return this;
	}

	_placeCamera() {
		const room = (this.scene && this.scene.room) || { w: 400, d: 300, h: 250 };
		const camera = this.camera;
		if (this.mode === "eye") {
			camera.fov = this.eye.fov;
			camera.near = 5;
			const yaw = (this.eye.yaw * Math.PI) / 180;
			const pitch = (this.eye.pitch * Math.PI) / 180;
			const eyeWorld = [this.eye.x, this.eye.y, this.eye.height];
			const forward = [
				Math.cos(pitch) * Math.cos(yaw),
				Math.cos(pitch) * Math.sin(yaw),
				Math.sin(pitch),
			];
			camera.position.copy(this._toThree(eyeWorld));
			camera.up.set(0, 1, 0);
			camera.lookAt(this._toThree([
				eyeWorld[0] + forward[0] * 100,
				eyeWorld[1] + forward[1] * 100,
				eyeWorld[2] + forward[2] * 100,
			]));
		} else {
			camera.fov = 42;
			camera.near = 10;
			const target = this.orbit.target || [room.w / 2, room.d / 2, room.h * 0.3];
			const yaw = (this.orbit.yaw * Math.PI) / 180;
			const pitch = (Math.max(4, Math.min(85, this.orbit.pitch)) * Math.PI) / 180;
			const r = this.orbit.distance;
			camera.position.copy(this._toThree([
				target[0] + Math.cos(pitch) * Math.cos(yaw) * r,
				target[1] + Math.cos(pitch) * Math.sin(yaw) * r,
				target[2] + Math.sin(pitch) * r,
			]));
			camera.up.set(0, 1, 0);
			camera.lookAt(this._toThree(target));
		}
		camera.updateProjectionMatrix();
	}

	/** World (z up) to the renderer's frame (y up). */
	_toThree(point) {
		return new this.THREE.Vector3(point[0], point[2], -point[1]);
	}

	/** Fit the orbit camera around whatever the scene holds. */
	frame() {
		const room = (this.scene && this.scene.room) || { w: 400, d: 300, h: 250 };
		const span = Math.max(room.w, room.d, room.h);
		this.orbit.distance = span * 1.5;
		this.orbit.target = [room.w / 2, room.d / 2, room.h * 0.3];
		this.request();
		return this;
	}

	// ---------------------------------------------------------------- picking
	select(id) {
		this.selected = id;
		this._applySelection();
		this.request();
		if (this.options.onSelect) {
			const mesh = this._objects.find((m) => m.userData.id === id);
			this.options.onSelect(mesh ? mesh.userData.box : null);
		}
		return this;
	}

	_applySelection() {
		if (!this.THREE) return;
		for (const mesh of this._objects) {
			const on = this.selected && mesh.userData.id === this.selected;
			mesh.material.emissiveIntensity = 1;
			if (on) {
				if (!mesh.userData.outline) {
					const edges = new this.THREE.LineSegments(
						new this.THREE.EdgesGeometry(mesh.geometry),
						new this.THREE.LineBasicMaterial({ color: 0x0f1419, linewidth: 2 })
					);
					mesh.add(edges);
					mesh.userData.outline = edges;
				}
				mesh.userData.outline.visible = true;
			} else if (mesh.userData.outline) {
				mesh.userData.outline.visible = false;
			}
		}
	}

	_pick(event) {
		const THREE = this.THREE;
		const rect = this.canvas.getBoundingClientRect();
		const pointer = new THREE.Vector2(
			((event.clientX - rect.left) / rect.width) * 2 - 1,
			-((event.clientY - rect.top) / rect.height) * 2 + 1
		);
		const ray = this._ray || (this._ray = new THREE.Raycaster());
		ray.setFromCamera(pointer, this.camera);
		const hits = ray.intersectObjects(this._objects, false);
		return hits.length ? hits[0] : null;
	}

	/** Where a screen point lands on the floor, in world centimetres. */
	_groundAt(event) {
		const THREE = this.THREE;
		const rect = this.canvas.getBoundingClientRect();
		const pointer = new THREE.Vector2(
			((event.clientX - rect.left) / rect.width) * 2 - 1,
			-((event.clientY - rect.top) / rect.height) * 2 + 1
		);
		const ray = this._ray || (this._ray = new THREE.Raycaster());
		ray.setFromCamera(pointer, this.camera);
		const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
		const hit = new THREE.Vector3();
		if (!ray.ray.intersectPlane(plane, hit)) return null;
		return [hit.x, -hit.z];
	}

	/** Project a world point to CSS pixels inside the container. */
	project(point) {
		const vector = this._toThree(point).project(this.camera);
		const rect = this.canvas.getBoundingClientRect();
		return {
			x: ((vector.x + 1) / 2) * rect.width,
			y: ((1 - vector.y) / 2) * rect.height,
			visible: vector.z < 1,
		};
	}

	// ------------------------------------------------------------ interaction
	_bind() {
		let dragging = null;
		let last = [0, 0];
		let moved = false;
		let target = null;
		let lastGround = null;

		this.canvas.addEventListener("pointerdown", (event) => {
			moved = false;
			last = [event.clientX, event.clientY];
			this.canvas.focus();
			const hit = event.button === 0 ? this._pick(event) : null;
			if (hit && this.options.onMove && this.mode !== "eye") {
				target = hit.object.userData.id;
				this.selected = target;
				this._applySelection();
				dragging = event.shiftKey ? "raise" : "move";
				lastGround = this._groundAt(event);
			} else {
				dragging = "look";
			}
			this.canvas.setPointerCapture(event.pointerId);
		});

		this.canvas.addEventListener("pointermove", (event) => {
			if (!dragging) return;
			const dx = event.clientX - last[0];
			const dy = event.clientY - last[1];
			if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
			last = [event.clientX, event.clientY];

			if (dragging === "look") {
				if (this.mode === "eye") {
					this.eye.yaw += dx * 0.22;
					this.eye.pitch = Math.min(80, Math.max(-80, this.eye.pitch - dy * 0.2));
				} else {
					this.orbit.yaw -= dx * 0.4;
					this.orbit.pitch = Math.min(85, Math.max(4, this.orbit.pitch + dy * 0.3));
				}
				this.request();
			} else if (dragging === "move") {
				const ground = this._groundAt(event);
				if (ground && lastGround) {
					this.options.onMove(target, {
						dx: ground[0] - lastGround[0],
						dy: ground[1] - lastGround[1],
						dz: 0,
					});
				}
				lastGround = ground;
			} else if (dragging === "raise") {
				const room = (this.scene && this.scene.room) || { h: 250 };
				this.options.onMove(target, { dx: 0, dy: 0, dz: (-dy * room.h) / 400 });
			}
		});

		const stop = () => {
			if ((dragging === "move" || dragging === "raise") && this.options.onMoveEnd) {
				this.options.onMoveEnd(target);
			}
			dragging = null;
			target = null;
		};
		this.canvas.addEventListener("pointerup", (event) => {
			if (!moved) {
				const hit = this._pick(event);
				if (hit) this.select(hit.object.userData.id);
			}
			stop();
		});
		this.canvas.addEventListener("pointercancel", stop);

		this.canvas.addEventListener("contextmenu", (event) => {
			event.preventDefault();
			const hit = this._pick(event);
			if (hit && this.options.onContext) {
				this.select(hit.object.userData.id);
				this.options.onContext(hit.object.userData.box, {
					x: event.clientX,
					y: event.clientY,
				});
			}
		});

		this.canvas.addEventListener("wheel", (event) => {
			event.preventDefault();
			if (this.mode === "eye") this.walk(event.deltaY > 0 ? -16 : 16, 0);
			else this.zoom(event.deltaY > 0 ? 0.9 : 1.11);
		}, { passive: false });

		this.canvas.addEventListener("keydown", (event) => {
			const key = String(event.key).toLowerCase();
			if (this.mode !== "eye") return;
			if (!"zqsdwa".includes(key) && !key.startsWith("arrow")) return;
			this._keys.add(key);
			this._pump();
			event.preventDefault();
		});
		this.canvas.addEventListener("keyup", (event) => this._keys.delete(String(event.key).toLowerCase()));
		this.canvas.addEventListener("blur", () => this._keys.clear());
	}

	/** Keep stepping while a movement key is held. */
	_pump() {
		if (this._pumping) return;
		this._pumping = true;
		const step = () => {
			if (!this._keys.size || this.mode !== "eye") {
				this._pumping = false;
				return;
			}
			const forward = (this._keys.has("z") || this._keys.has("w") || this._keys.has("arrowup") ? 1 : 0)
				- (this._keys.has("s") || this._keys.has("arrowdown") ? 1 : 0);
			const strafe = (this._keys.has("d") || this._keys.has("arrowright") ? 1 : 0)
				- (this._keys.has("q") || this._keys.has("a") || this._keys.has("arrowleft") ? 1 : 0);
			if (forward || strafe) this.walk(forward * 6, strafe * 6);
			requestAnimationFrame(step);
		};
		requestAnimationFrame(step);
	}

	_observeSize() {
		const resize = () => {
			const width = this.container.clientWidth || 800;
			const height = this.container.clientHeight || 500;
			this.renderer.setSize(width, height, false);
			this.camera.aspect = width / Math.max(1, height);
			this.camera.updateProjectionMatrix();
			this.request();
		};
		resize();
		if (typeof ResizeObserver !== "undefined") {
			this._resizeObserver = new ResizeObserver(resize);
			this._resizeObserver.observe(this.container);
		} else {
			window.addEventListener("resize", resize);
		}
	}

	// -------------------------------------------------------------- rendering
	/** Draw one frame on the next tick — never a permanent render loop. */
	request() {
		if (!this.ready || this._needsFrame) return this;
		this._needsFrame = true;
		requestAnimationFrame(() => {
			this._needsFrame = false;
			this._placeCamera();
			this.renderer.render(this.world, this.camera);
			if (this.options.onFrame) this.options.onFrame();
		});
		return this;
	}

	dispose() {
		if (this._resizeObserver) this._resizeObserver.disconnect();
		if (this.renderer) this.renderer.dispose();
		this._materials.forEach((material) => material.dispose());
		this._materials.clear();
		if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
		this.ready = false;
	}
}

// the augmented-reality viewer needs the same loader and the same notion of a
// texture tile, and there is no reason for two copies of either
if (typeof window !== "undefined") {
	window.RetailGL = RetailGL;
	window.RetailGLShared = { loadThree: loadThree, tiles: TILE_FALLBACK, assetRoot: ASSET_ROOT };
}
if (typeof module !== "undefined") module.exports = RetailGL;
